// Mock data for FortressPoint IT Solutions clone
import {
  ShieldCheck, Network, Cpu, Cloud, Users, Bot, GraduationCap,
  MessagesSquare, Building2, Lightbulb, Sparkles, Award, Target,
  Zap
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const IMG = (name) => `${BACKEND_URL}/api/images/${name}.png`;

export const stats = [
  { icon: Award, value: "500+", label: "Projects Delivered" },
  { icon: Zap, value: "15+", label: "Years Experience" },
  { icon: Sparkles, value: "98%", label: "Client Satisfaction" },
  { icon: Network, value: "24/7", label: "Support Available" },
];

export const services = [
  {
    slug: "it-consulting-strategy",
    icon: Lightbulb,
    title: "IT Consulting & Strategy",
    description: "Guiding clients through complex technology decisions with tailored IT solutions that optimize performance and security. Strategic planning for digital transformation and technology roadmap development.",
    features: ["Technology Assessment", "Strategic Planning", "Solution Architecture"],
    image: IMG("service_it_consulting_strategy"),
    accent: "from-sky-500 to-blue-500",
  },
  {
    slug: "unified-collaboration",
    icon: MessagesSquare,
    title: "Unified Collaboration Technologies",
    description: "Enabling seamless communication and teamwork across organizations with modern unified communications platforms, VoIP solutions, and integrated collaboration tools.",
    features: ["Unified Communications", "Video Conferencing", "Team Collaboration Tools"],
    image: IMG("service_unified_collaboration"),
    accent: "from-cyan-500 to-teal-500",
  },
  {
    slug: "security-compliance",
    icon: ShieldCheck,
    title: "IT Security & Compliance",
    description: "Advanced protection for networks and data with industry-leading security practices. Meeting stringent security requirements for government contracts and regulated industries.",
    features: ["Threat Protection", "Security Monitoring", "Compliance Management"],
    image: IMG("service_security_compliance"),
    accent: "from-blue-500 to-indigo-500",
  },
  {
    slug: "network-infrastructure",
    icon: Network,
    title: "Robust & Secured Networking",
    description: "Building solid and dependable network foundations with enterprise-grade infrastructure. Secure, scalable networking technologies designed for mission-critical operations.",
    features: ["Network Architecture", "Secure Infrastructure", "High Availability"],
    image: IMG("service_network_infrastructure"),
    accent: "from-sky-500 to-cyan-500",
  },
  {
    slug: "ai-technologies",
    icon: Cpu,
    title: "AI Technologies & Innovation",
    description: "Leveraging emerging AI technologies to help businesses innovate and gain a competitive edge. Intelligent automation and AI-driven solutions for operational excellence.",
    features: ["AI Integration", "Intelligent Automation", "Predictive Analytics"],
    image: IMG("service_ai_technologies"),
    accent: "from-cyan-500 to-blue-500",
  },
  {
    slug: "small-business-support",
    icon: Building2,
    title: "Small Business IT Support",
    description: "Specialized IT support for small businesses, retail stores, small home offices, and restaurants. Affordable, reliable technology solutions designed to help small businesses thrive and compete.",
    features: ["Retail IT Solutions", "Restaurant Technology", "Small Office Support"],
    image: IMG("service_small_business_support"),
    accent: "from-teal-500 to-cyan-500",
  },
  {
    slug: "agentic-ai",
    icon: Bot,
    title: "Agentic AI & Autonomous Workforce",
    description: "Building intelligent agentic systems that don't just talk—they act. Autonomous contact center orchestration across Cisco and Genesys platforms, multi-agent systems that solve enterprise problems, and AI model fine-tuning for specialized industry verticals.",
    features: ["Autonomous Contact Center Orchestration", "Multi-Agent System (MAS) Design", "AI Model Fine-Tuning for Vertical CX"],
    image: IMG("service_agentic_ai"),
    accent: "from-indigo-500 to-blue-500",
  },
  {
    slug: "cloud-migration",
    icon: Cloud,
    title: "Intelligent Cloud Migration & Hybrid Architectures",
    description: "Migrating legacy on-premise contact centers to modern cloud platforms while integrating Microsoft AI and Azure OpenAI at the core. Network-level AI optimization ensuring infrastructure can handle real-time AI voice and video processing demands.",
    features: ["Cognitive Cloud Transitioning", "Genesys Cloud Migration", "Network-Level AI Optimization"],
    image: IMG("service_cloud_migration"),
    accent: "from-sky-500 to-indigo-500",
  },
  {
    slug: "cx-strategy",
    icon: Target,
    title: "Advanced CX Strategy & Conversational Design",
    description: "SME-level architectural consulting to transform customer journeys. Conversational architecture audits identifying friction points where agentic AI can replace clunky IVR systems, with ethical AI and compliance frameworks ensuring adherence to GDPR, HIPAA, and brand safety guidelines.",
    features: ["Conversational Architecture Audits", "Customer Journey Optimization", "Ethical AI & Compliance Frameworks"],
    image: IMG("service_cx_strategy"),
    accent: "from-blue-500 to-cyan-500",
  },
  {
    slug: "enterprise-training",
    icon: GraduationCap,
    title: "SME-Led Enterprise Training & Workshops",
    description: "Executive-level workshops and training programs led by certified subject matter experts. AI readiness consulting to assess maturity curves, and prompt engineering training for enterprise teams to effectively manage and optimize AI agents.",
    features: ["AI Readiness Consulting", "Prompt Engineering for Enterprise", "Executive Workshops"],
    image: IMG("service_enterprise_training"),
    accent: "from-cyan-500 to-sky-500",
  },
];

export const advantages = [
  { icon: ShieldCheck, title: "Fortress-Grade Security", desc: "Advanced protection combining the strength of a fortress with sophisticated digital security. Meeting stringent requirements for government contracts and regulated industries." },
  { icon: Award, title: "Certified Subject Matter Experts", desc: "Cisco certified professionals with extensive certifications across Microsoft, Fortinet, Palo Alto, Juniper, Aruba, and other leading platforms. Proven expertise backed by industry-recognized credentials." },
  { icon: Sparkles, title: "Full-Spectrum Expertise", desc: "Comprehensive IT services spanning consulting, unified collaboration, security, networking, and AI technologies. Complete digital ecosystem solutions." },
  { icon: Users, title: "Industry Specialization", desc: "Deep expertise serving healthcare, transportation, hospitality, oil & gas, insurance, and government sectors with tailored solutions for each industry." },
];

export const caseStudies = [
  {
    category: "Healthcare",
    title: "Multi-Site Network Transformation for Regional Healthcare System",
    challenge: "A 12-hospital network struggled with legacy infrastructure causing frequent outages and compliance risks.",
    solution: "Deployed Cisco SD-WAN with Palo Alto security, implementing zero-trust architecture across all sites.",
    metrics: [ { value: "99.99%", label: "Uptime Achieved" }, { value: "60%", label: "Cost Reduction" }, { value: "0", label: "Security Incidents" } ],
    tech: ["Cisco SD-WAN", "Palo Alto", "Microsoft Azure"],
    image: IMG("service_network_infrastructure"),
  },
  {
    category: "Financial Services",
    title: "Enterprise Cloud Migration for Fortune 500 Bank",
    challenge: "Critical banking applications required migration to hybrid cloud while maintaining regulatory compliance.",
    solution: "Designed hybrid architecture with Microsoft Azure and on-premise Fortinet security, ensuring seamless failover.",
    metrics: [ { value: "40%", label: "Performance Boost" }, { value: "$2.1M", label: "Annual Savings" }, { value: "100%", label: "Compliance" } ],
    tech: ["Microsoft Azure", "Fortinet", "Juniper"],
    image: IMG("service_cloud_migration"),
  },
  {
    category: "Retail",
    title: "AI-Powered Contact Center Transformation",
    challenge: "National retailer faced 45-minute average hold times and declining customer satisfaction scores.",
    solution: "Implemented agentic AI workforce with conversational design, integrating with existing CRM systems.",
    metrics: [ { value: "78%", label: "Faster Resolution" }, { value: "94%", label: "CSAT Score" }, { value: "24/7", label: "Support Coverage" } ],
    tech: ["Agentic AI", "Cisco Webex", "Aruba Wireless"],
    image: IMG("service_agentic_ai"),
  },
  {
    category: "Manufacturing",
    title: "Secure IoT Network for Smart Factory Initiative",
    challenge: "Global manufacturer needed to connect 50,000+ IoT devices while protecting against cyber threats.",
    solution: "Built segmented network with Aruba edge infrastructure and Fortinet security fabric for OT protection.",
    metrics: [ { value: "50K+", label: "Devices Connected" }, { value: "35%", label: "Efficiency Gain" }, { value: "Zero", label: "Breaches" } ],
    tech: ["Aruba", "Fortinet", "Juniper"],
    image: IMG("service_security_compliance"),
  },
];

export const testimonials = [
  { industry: "Healthcare", quote: "FortressPoint transformed our entire network infrastructure across 12 hospitals. Their team's expertise in healthcare compliance and 24/7 support has been invaluable. We've seen 99.99% uptime since implementation.", author: "Dr. Sarah Mitchell", role: "CTO, Regional Healthcare Network", initials: "DSM" },
  { industry: "Financial Services", quote: "The cloud migration was seamless. FortressPoint's certified engineers understood our security requirements from day one. They saved us over $2M annually while improving our infrastructure resilience.", author: "James Richardson", role: "VP of Technology, Fortune 500 Bank", initials: "JR" },
  { industry: "Restaurant", quote: "As a small restaurant chain, we needed reliable IT without enterprise costs. FortressPoint delivered POS integration, secure networking, and ongoing support that just works. They truly understand small business needs.", author: "Maria Gonzalez", role: "Owner, Casa Fresca Restaurant Group", initials: "MG" },
  { industry: "Retail", quote: "Their AI-powered contact center solution reduced our average handle time by 40% and dramatically improved customer satisfaction. The team's expertise in conversational design is unmatched.", author: "Robert Chen", role: "Director of Customer Experience, National Retailer", initials: "RC" },
  { industry: "Government", quote: "Meeting government security compliance seemed impossible until we partnered with FortressPoint. Their fortress-grade security approach and certified SMEs made our federal contract a reality.", author: "Patricia Williams", role: "IT Director, State Government Agency", initials: "PW" },
  { industry: "Transportation", quote: "The unified collaboration platform FortressPoint implemented connected our 15 global offices seamlessly. Video conferencing, VoIP, and team collaboration all work flawlessly now.", author: "Michael Okonkwo", role: "Global Operations Manager, Logistics Corp", initials: "MO" },
];

export const partners = [
  "Cisco", "Microsoft", "Fortinet", "Palo Alto", "Juniper", "Aruba", "AWS", "Google Cloud", "Azure OpenAI", "Genesys",
];

export const teamMoments = [
  { title: "Solution Architecture Whiteboarding", subtitle: "SMEs whiteboarding enterprise network & cloud diagrams—WAN links, dual-homing, DR sites and business continuity zones—for a Fortune 500 client.", image: IMG("team_whiteboarding_network_design") },
  { title: "AI Model Fine-Tuning Session", subtitle: "Prompt engineers refining agentic AI for a national retailer's contact center.", image: IMG("team_ai_finetuning") },
  { title: "Security Operations Command Center", subtitle: "24/7 monitoring across healthcare, finance and government workloads.", image: IMG("team_soc_command_center") },
  { title: "Cloud Migration Design Sprint", subtitle: "Engineers designing hybrid architectures with Azure OpenAI at the core.", image: IMG("team_cloud_design_sprint") },
];

export const contactInfo = {
  emails: ["info@FortressPointITSolutions.com", "support@FortressPointITSolutions.com"],
  phone: "+1 (346) 321-0387",
  address: ["539 West Commerce Street, Suite 1517", "Dallas, TX 75208"],
  hours: [
    { day: "Monday - Friday", time: "8:00 AM - 6:00 PM" },
    { day: "Saturday", time: "9:00 AM - 2:00 PM" },
    { day: "Sunday", time: "Closed" },
  ],
};

export const timeSlots = [
  "09:00 AM", "09:30 AM", "10:00 AM", "10:30 AM", "11:00 AM",
  "11:30 AM", "01:00 PM", "01:30 PM", "02:00 PM", "02:30 PM",
  "03:00 PM", "03:30 PM", "04:00 PM", "04:30 PM", "05:00 PM",
];
